/**
 * DATOS MOCK PARA DESARROLLO - Sistema VALIDA
 *
 * Este archivo contiene datos de ejemplo para facilitar el desarrollo y testing
 * del módulo de plantillas cuando el backend no está disponible.
 *
 * DIRECTIVA DE DESARROLLO:
 * - Usar solo para desarrollo y testing
 * - Nunca incluir datos reales de producción
 * - Mantener sincronizado con estructura real de datos
 * - Documentar cualquier cambio en este archivo
 *
 * Última actualización: 28 de noviembre de 2025
 */

export const mockTemplates = [
  {
    id: 1,
    name: 'Catastro Rural',
    description: 'Plantilla para datos catastrales rurales según normativa IGAC',
    category: 'Catastro',
    status: 'active',
    createdBy: 'admin',
    createdAt: '2024-01-15',
    lastModified: '2024-02-10',
    company: 'Empresa Demo',
    gdbStructure: {
      featureClasses: [
        {
          name: 'Predios',
          type: 'Polygon',
          fields: [
            { name: 'OBJECTID', type: 'OID', required: true },
            { name: 'CODIGO_PREDIO', type: 'String', length: 20, required: true },
            { name: 'AREA_HA', type: 'Double', required: true },
            { name: 'PROPIETARIO', type: 'String', length: 100 },
            { name: 'SHAPE', type: 'Geometry', required: true }
          ],
          validations: ['Topología', 'Dominio', 'Sintáctica']
        },
        {
          name: 'Vias',
          type: 'Polyline',
          fields: [
            { name: 'OBJECTID', type: 'OID', required: true },
            { name: 'TIPO_VIA', type: 'String', length: 50, required: true },
            { name: 'ESTADO', type: 'String', length: 20 },
            { name: 'SHAPE', type: 'Geometry', required: true }
          ],
          validations: ['Topología', 'Sintáctica']
        }
      ],
      tables: [
        {
          name: 'Propietarios',
          fields: [
            { name: 'OBJECTID', type: 'OID', required: true },
            { name: 'CEDULA', type: 'String', length: 20, required: true },
            { name: 'NOMBRE', type: 'String', length: 100, required: true },
            { name: 'TELEFONO', type: 'String', length: 15 }
          ],
          validations: ['Sintáctica', 'Relacional']
        }
      ],
      domains: [
        {
          name: 'DOM_TIPO_VIA',
          type: 'CodedValue',
          values: ['Principal', 'Secundaria', 'Terciaria', 'Sendero']
        }
      ],
      relationships: [
        {
          name: 'Predios_Propietarios',
          originTable: 'Predios',
          destinationTable: 'Propietarios',
          cardinality: 'OneToMany'
        }
      ]
    }
  },
  {
    id: 2,
    name: 'Catastro Urbano',
    description: 'Plantilla para levantamiento catastral en zonas urbanas',
    category: 'Catastro',
    status: 'active',
    createdBy: 'juan.perez',
    createdAt: '2024-02-01',
    lastModified: '2024-02-15',
    company: 'Empresa Demo',
    gdbStructure: {
      featureClasses: [
        {
          name: 'Manzanas',
          type: 'Polygon',
          fields: [
            { name: 'OBJECTID', type: 'OID', required: true },
            { name: 'COD_MANZANA', type: 'String', length: 15, required: true },
            { name: 'BARRIO', type: 'String', length: 50 },
            { name: 'SHAPE', type: 'Geometry', required: true }
          ],
          validations: ['Topología', 'Sintáctica']
        }
      ],
      tables: [],
      domains: [],
      relationships: []
    }
  },
  {
    id: 3,
    name: 'Infraestructura Vial',
    description: 'Template para inventario de infraestructura vial',
    category: 'Infraestructura',
    status: 'draft',
    createdBy: 'admin',
    createdAt: '2024-02-20',
    lastModified: '2024-02-20',
    company: 'Empresa Demo',
    gdbStructure: {
      featureClasses: [],
      tables: [],
      domains: [],
      relationships: []
    }
  },
  {
    id: 4,
    name: 'Redes Eléctricas',
    description: 'Plantilla para el diseño y mantenimiento de redes eléctricas de media y baja tensión.',
    category: 'Servicios Públicos',
    status: 'active',
    createdBy: 'ana.gomez',
    createdAt: '2024-03-01',
    lastModified: '2024-03-05',
    company: 'Empresa Demo',
    gdbStructure: {
      featureClasses: [
        { name: 'Postes', type: 'Point' },
        { name: 'Transformadores', type: 'Point' },
        { name: 'LineasMT', type: 'Polyline' }
      ],
      tables: [],
      domains: [
        {
          name: 'DOM_VOLTAJE',
          type: 'CodedValue',
          values: ['13.2kV', '34.5kV', '115kV']
        }
      ],
      relationships: []
    }
  },
  {
    id: 5,
    name: 'Inventario Forestal',
    description: 'Modelo de datos para el censo de especies arbóreas en áreas protegidas.',
    category: 'Medio Ambiente',
    status: 'draft',
    createdBy: 'carlos.ruiz',
    createdAt: '2024-03-10',
    lastModified: '2024-03-12',
    company: 'Constructora ABC',
    gdbStructure: {
      featureClasses: [
        {
          name: 'Arboles',
          type: 'Point',
          fields: [{ name: 'ESPECIE', type: 'String' }]
        }
      ],
      tables: [],
      domains: [],
      relationships: []
    }
  }
];

// Configuración del sistema de fallback
// Los datos de prueba se controlan desde public/config.json
let appConfig = null;

// Función para cargar configuración desde archivo
const loadAppConfig = async () => {
  if (appConfig) return appConfig;

  try {
    const response = await fetch(`${process.env.PUBLIC_URL}/config.json`);
    if (!response.ok) {
      throw new Error('No se pudo cargar config.json');
    }
    appConfig = await response.json();
    return appConfig;
  } catch (error) {
    console.warn('Error cargando configuración, usando valores por defecto:', error);
    // Valores por defecto si no se puede cargar el archivo
    appConfig = {
      features: {
        enableMockData: process.env.NODE_ENV === 'development', // Solo en desarrollo por defecto
        showMockIndicators: true,
        logFallback: true
      }
    };
    return appConfig;
  }
};

export const MOCK_CONFIG = {
  // Configuración asíncrona - se carga desde config.json
  get enabled() {
    return appConfig?.features?.enableMockData && process.env.NODE_ENV === 'development';
  },
  get showIndicators() {
    return appConfig?.features?.showMockIndicators ?? true;
  },
  get logFallback() {
    return appConfig?.features?.logFallback ?? true;
  },
  version: '1.0.0',
  lastUpdated: '2026-01-15',
  // Método para inicializar configuración
  initialize: loadAppConfig
};

// Función helper para verificar si estamos usando datos mock
export const isUsingMockData = (data) => {
  if (!Array.isArray(data)) return false;
  return data.some(item => item.id && mockTemplates.some(mock => mock.id === item.id));
};

// Función helper para agregar metadata de mock a los datos
export const addMockMetadata = (data) => {
  return data.map(item => ({
    ...item,
    _isMockData: true,
    _mockVersion: MOCK_CONFIG.version,
    _lastUpdated: MOCK_CONFIG.lastUpdated
  }));
};